
import React, { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/base/WhatsAppButton';
import BackToTopButton from '../../components/base/BackToTopButton';
import CookieBanner from '../../components/base/CookieBanner';
import SEOHead from '../../components/seo/SEOHead';

export default function FAQ() {
  const [openItems, setOpenItems] = useState<{ [key: number]: boolean }>({});

  const toggleItem = (index: number) => {
    setOpenItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const faqs = [
    {
      question: "¿En qué zonas de Girona ofrecéis servicios de pintura?",
      answer: "Ofrecemos nuestros servicios de pintura profesional en Olot y toda la comarca de la Garrotxa, así como en toda la provincia de Girona. Nuestras principales zonas de actuación incluyen Olot, Girona capital, Figueres, Blanes, Sant Feliu de Guíxols, Lloret de Mar, Ripoll, Puigcerdà, La Bisbal d'Empordà, Palafrugell, Roses y Cadaqués, entre otras localidades."
    },
    {
      question: "¿Cuánto tiempo tardáis en realizar un trabajo de pintura?",
      answer: "El tiempo de ejecución depende del tipo y tamaño del proyecto. Una habitación estándar suele tardar 1-2 días, una vivienda completa entre 1-2 semanas, y proyectos comerciales pueden variar según su envergadura. Siempre proporcionamos un cronograma detallado en el presupuesto inicial y nos comprometemos a cumplir los plazos acordados."
    },
    {
      question: "¿Qué tipo de pinturas utilizáis en vuestros trabajos?",
      answer: "Utilizamos exclusivamente pinturas de primera calidad de marcas reconocidas como Bruguer, Jotun, Titan y Sherwin Williams. Seleccionamos el tipo de pintura más adecuado para cada proyecto: pinturas plásticas para interiores, pinturas acrílicas para exteriores, esmaltes sintéticos para maderas y metales, y pinturas especiales para ambientes húmedos como cocinas y baños."
    },
    {
      question: "¿Incluís la preparación de superficies en el presupuesto?",
      answer: "Sí, nuestro servicio completo incluye toda la preparación necesaria de superficies: limpieza, lijado, reparación de pequeñas imperfecciones, aplicación de imprimaciones cuando es necesario, y protección de mobiliario y suelos. La preparación adecuada es fundamental para obtener un acabado perfecto y duradero."
    },
    {
      question: "¿Ofrecéis garantía en vuestros trabajos de pintura?",
      answer: "Sí, ofrecemos garantía en todos nuestros trabajos. Para pintura interior ofrecemos garantía de 2 años, para pintura exterior hasta 5 años (dependiendo del tipo de pintura utilizada), y para trabajos especializados como lacados ofrecemos garantía de 3 años. La garantía cubre tanto materiales como mano de obra."
    },
    {
      question: "¿Hacéis presupuestos gratuitos?",
      answer: "Sí, todos nuestros presupuestos son completamente gratuitos y sin compromiso. Nos desplazamos a tu ubicación en Olot, Girona o cualquier punto de la Garrotxa para evaluar el proyecto personalmente. El presupuesto incluye materiales, mano de obra, preparación de superficies y limpieza final, sin costes ocultos."
    },
    {
      question: "¿Trabajáis en fin de semana o fuera del horario laboral?",
      answer: "Sí, nos adaptamos a las necesidades de nuestros clientes. Para proyectos residenciales podemos trabajar en fin de semana, y para empresas que no pueden interrumpir su actividad, ofrecemos servicios en horarios nocturnos o festivos. Estos servicios especiales pueden tener un pequeño recargo que se especifica en el presupuesto."
    },
    {
      question: "¿Qué medidas de seguridad aplicáis durante los trabajos?",
      answer: "Aplicamos todas las medidas de seguridad requeridas: uso de equipos de protección individual, señalización de zonas de trabajo, protección de mobiliario y suelos, ventilación adecuada durante la aplicación de pinturas, y cumplimiento de todas las normativas de seguridad laboral. Contamos con seguro de responsabilidad civil para todos nuestros trabajos."
    },
    {
      question: "¿Podéis ayudarnos a elegir colores y acabados?",
      answer: "Por supuesto, ofrecemos asesoramiento completo en selección de colores y acabados. Nuestro equipo tiene amplia experiencia en interiorismo y puede sugerir combinaciones de colores que se adapten a tus gustos y al estilo de tu hogar o negocio. Proporcionamos muestras de color y realizamos pruebas en pequeñas áreas antes de proceder con todo el trabajo."
    },
    {
      question: "¿Trabajáis con pinturas ecológicas y libres de tóxicos?",
      answer: "Sí, disponemos de una amplia gama de pinturas ecológicas, libres de compuestos orgánicos volátiles (COV) y tóxicos. Estas pinturas son especialmente recomendables para habitaciones infantiles, personas con alergias o sensibilidades químicas, y espacios cerrados. Son igual de duraderas que las pinturas convencionales pero más respetuosas con el medio ambiente y la salud."
    },
    {
      question: "¿Ofrecéis servicios de mantenimiento posteriores?",
      answer: "Sí, ofrecemos servicios de mantenimiento y retoques posteriores. Recomendamos revisiones anuales para pintura exterior y cada 2-3 años para interiores. Ofrecemos contratos de mantenimiento para empresas y comunidades, incluyendo servicios de limpieza especializada, retoques menores y repintado parcial cuando sea necesario."
    },
    {
      question: "¿Cómo puedo solicitar un presupuesto para mi proyecto?",
      answer: "Puedes solicitar tu presupuesto gratuito de varias formas: llamando directamente al 609 93 65 90, enviando un WhatsApp al mismo número, a través del formulario de contacto de nuestra web, o enviando un email a info@pintolot.com.es. Nos pondremos en contacto contigo en menos de 24 horas para concertar una visita y evaluar tu proyecto personalmente."
    }
  ];

  const seoSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <SEOHead
        title="Preguntas Frecuentes sobre Pintura en Olot | FAQ PintoLot"
        description="Resuelve todas tus dudas sobre servicios de pintura en Olot y Garrotxa. Preguntas frecuentes sobre presupuestos, garantías, tiempos y materiales de pintura."
        keywords="preguntas pintura Olot, FAQ pintores Garrotxa, dudas servicios pintura, presupuesto pintura Girona"
        canonical={`${import.meta.env.VITE_SITE_URL}/faq`}
        ogTitle="Preguntas Frecuentes - PintoLot Servicios de Pintura"
        ogDescription="Encuentra respuestas a las preguntas más comunes sobre nuestros servicios de pintura en Olot y la Garrotxa."
        schema={seoSchema}
      />
      <Header />
      <main>
        {/* Hero Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Preguntas frecuentes sobre servicios de pintura en Olot
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Resolvemos todas tus dudas sobre nuestros servicios de pintura profesional para particulares y empresas en Olot, Girona y toda la comarca de la Garrotxa.
            </p>
          </div>
        </section>
        {/* FAQ Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-gray-50 rounded-lg border border-gray-200">
                  <button
                    onClick={() => toggleItem(index)}
                    className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-100 transition-colors cursor-pointer"
                  >
                    <h3 className="text-lg font-semibold text-gray-900 pr-4">
                      {faq.question}
                    </h3>
                    <div className="flex-shrink-0">
                      <i className={`ri-${openItems[index] ? 'subtract' : 'add'}-line text-[#51b038] text-xl`}></i>
                    </div>
                  </button>
                  {openItems[index] && (
                    <div className="px-6 pb-4">
                      <p className="text-gray-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Still Have Questions */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                ¿Aún tienes dudas?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Si no has encontrado la respuesta a tu pregunta, no dudes en contactar con nosotros. Estaremos encantados de resolver cualquier duda sobre nuestros servicios de pintura en Olot y la Garrotxa.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-lg shadow-lg text-center">
                <div className="w-16 h-16 bg-[#51b038] rounded-lg flex items-center justify-center mb-6 mx-auto">
                  <i className="ri-phone-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-semibold mb-4">Llámanos</h3>
                <p className="text-gray-600 mb-4">
                  Habla directamente con nuestro equipo para resolver tus dudas al instante.
                </p>
                <a 
                  href="tel:609936590" 
                  className="text-[#51b038] hover:text-[#7a806a] font-semibold text-lg cursor-pointer"
                >
                  609 93 65 90
                </a>
              </div>

              <div className="bg-white p-8 rounded-lg shadow-lg text-center">
                <div className="w-16 h-16 bg-[#51b038] rounded-lg flex items-center justify-center mb-6 mx-auto">
                  <i className="fab fa-whatsapp text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-semibold mb-4">WhatsApp</h3>
                <p className="text-gray-600 mb-4">
                  Envíanos un mensaje y te responderemos lo antes posible.
                </p>
                <a 
                  href="https://wa.me/34609936590" 
                  className="text-[#51b038] hover:text-[#7a806a] font-semibold text-lg cursor-pointer"
                >
                  Escribir Mensaje
                </a>
              </div>

              <div className="bg-white p-8 rounded-lg shadow-lg text-center">
                <div className="w-16 h-16 bg-[#51b038] rounded-lg flex items-center justify-center mb-6 mx-auto">
                  <i className="ri-mail-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-semibold mb-4">Email</h3>
                <p className="text-gray-600 mb-4">
                  Escríbenos detalladamente tu consulta y te responderemos en 24h.
                </p>
                <a 
                  href="mailto:info@pintolot.com.es" 
                  className="text-[#51b038] hover:text-[#7a806a] font-semibold text-lg cursor-pointer"
                >
                  Enviar Email
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Tips Section */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Consejos útiles para tu proyecto de pintura
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Algunos consejos profesionales que te ayudarán a preparar tu proyecto de pintura y obtener los mejores resultados en tu hogar o negocio en Olot y la Garrotxa.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gray-50 p-8 rounded-lg">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <i className="ri-lightbulb-line text-[#51b038] text-xl mr-3"></i>
                  Preparación del espacio
                </h3>
                <ul className="space-y-3 text-gray-600">
                  <li>• Retira o protege todos los muebles y objetos decorativos</li>
                  <li>• Asegúrate de tener buena ventilación durante y después del trabajo</li>
                  <li>• Planifica el proyecto con tiempo suficiente, especialmente en época de lluvia</li>
                  <li>• Comunica cualquier problemática específica (humedades, grietas, etc.)</li>
                </ul>
              </div>

              <div className="bg-gray-50 p-8 rounded-lg">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <i className="ri-palette-line text-[#51b038] text-xl mr-3"></i>
                  Selección de colores
                </h3>
                <ul className="space-y-3 text-gray-600">
                  <li>• Ten en cuenta la orientación y luminosidad natural de cada estancia</li>
                  <li>• Considera el uso de cada habitación al elegir el tipo de pintura</li>
                  <li>• Solicita muestras de color antes de decidir definitivamente</li>
                  <li>• Piensa en la durabilidad y facilidad de mantenimiento</li>
                </ul>
              </div>

              <div className="bg-gray-50 p-8 rounded-lg">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <i className="ri-calendar-line text-[#51b038] text-xl mr-3"></i>
                  Mejor época del año
                </h3>
                <ul className="space-y-3 text-gray-600">
                  <li>• La primavera y el otoño son ideales para pintura exterior</li>
                  <li>• Evita épocas de alta humedad para mejores resultados</li>
                  <li>• Para interiores, cualquier época es buena con buena ventilación</li>
                  <li>• Planifica con antelación en temporadas de alta demanda</li>
                </ul>
              </div>

              <div className="bg-gray-50 p-8 rounded-lg">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <i className="ri-shield-check-line text-[#51b038] text-xl mr-3"></i>
                  Cuidado posterior
                </h3>
                <ul className="space-y-3 text-gray-600">
                  <li>• Mantén una temperatura estable durante las primeras 48 horas</li>
                  <li>• Evita limpiar las superficies pintadas durante la primera semana</li>
                  <li>• Realiza revisiones anuales para detectar posibles retoques necesarios</li>
                  <li>• Conserva pequeñas cantidades de pintura para futuros retoques</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-20 bg-[#51b038]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              ¿Listo para empezar tu proyecto de pintura?
            </h2>
            <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
              Contacta con nosotros para recibir asesoramiento personalizado y un presupuesto gratuito para tu proyecto en Olot, Girona y la Garrotxa.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/contacto" 
                className="bg-white text-[#51b038] hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold text-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                Solicitar presupuesto gratuito
              </a>
              <a 
                href="tel:609936590" 
                className="border-2 border-white text-white hover:bg-white hover:text-[#51b038] px-8 py-4 rounded-lg font-semibold text-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                Llamar ahora
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
      <BackToTopButton />
      <CookieBanner />
    </>
  );
}
